import classNames from 'classnames'
import { Dayjs } from 'dayjs'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactCalendarTimeline, {
  DateHeader,
  Id,
  ReactCalendarGroupRendererProps,
  ReactCalendarTimelineProps,
  TimelineHeaders,
  TimelineMarkers,
  TimelineTimeSteps,
  Unit,
} from 'react-calendar-timeline'
import { ItemRendererProps } from 'react-calendar-timeline/dist/lib/items/Item'
import 'react-calendar-timeline/dist/style.css'
import { useTranslation } from 'react-i18next'

import { Card, Typography } from '@mui/material'

import GRCircularProgress from '../../../../components/GRCircularProgress/GRCircularProgress'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useMobileSize } from '../../../responsiveness/hooks/responsivenessHooks'
import { EMPTY_CALENDAR_GROUP_ID } from '../../hooks/liveEventsCalendarEvents'
import { LiveEventTagGroup } from '../../types/LiveEventTagGroup'
import {
  LiveEventsCalendarTimelineGroup,
  LiveEventsCalendarTimeLineItem,
  LiveEventsCalendarTimelineType,
  TrackingEventTimelineGroup,
  TrackingEventTimelineItem,
  TrackingEventWithGameTimelineGroup,
} from '../../types/LiveEvents'
import { TrackingEventTimeRange } from '../../types/TrackingEvents'
import { getNextCalendarHeaderUnit } from '../../utils/utils'
import './LiveEventsCalendar.scss'
import { LiveEventsCalendarGroup } from './LiveEventsCalendarGroup/LiveEventsCalendarGroup'
import { LiveEventsCalendarItem } from './LiveEventsCalendarItem/LiveEventsCalendarItem'
import { TimeRangeMarker } from './TimeRangeMarker/TimeRangeMarker'
import { useStickyCalendarItemTitles } from './useStickyCalendarItemTitles'

const today = +new Date()

//TODO: remove this override once the typing issue is fixed in the react-calendar-timeline package
const CustomTimelineMarkers = TimelineMarkers as FC<{ children: React.ReactNode }>

interface Props {
  trackingEventTimeRange: TrackingEventTimeRange
  groups: LiveEventsCalendarTimelineGroup[]
  items: LiveEventsCalendarTimeLineItem[]
  timeStart?: number
  timeEnd?: number
  minZoom?: number
  maxZoom?: number
  onItemClick: (clickedItem: TrackingEventTimelineItem) => void
  onGroupClick: (clickedGroup: TrackingEventTimelineGroup) => void
  onArchetypeClick: (clickedGroup: TrackingEventTimelineGroup) => void
  onCalendarTimeRangeChange?: ReactCalendarTimelineProps<LiveEventsCalendarTimeLineItem, LiveEventsCalendarTimelineGroup>['onTimeChange']
  isLoading?: boolean
  calendarHidden?: boolean
  lineHeight?: number
  sidebarHeader: JSX.Element
  datePrimaryHeaderHeight?: number
  showEmptyCalendarIndicator?: boolean
  groupByArchetype?: boolean
  loadedEventsTimeRange?: TrackingEventTimeRange
}

const LiveEventsCalendar: React.FC<Props> = ({
  trackingEventTimeRange,
  groups,
  items,
  timeStart,
  timeEnd,
  minZoom,
  maxZoom,
  onItemClick,
  onGroupClick,
  onArchetypeClick,
  onCalendarTimeRangeChange,
  isLoading,
  calendarHidden,
  lineHeight,
  sidebarHeader,
  showEmptyCalendarIndicator = true,
  groupByArchetype = false,
  loadedEventsTimeRange,
}) => {
  const { t } = useTranslation()
  const mobileSize = useMobileSize()
  const scrollRef = useRef<HTMLDivElement>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const headerTimeoutRef = useRef<number>()
  const mouseDownTime = useRef<number>(0)
  const userLanguage = useCurrentUserLanguage()
  const [primaryUnit, setPrimaryUnit] = useState<Unit | 'primaryHeader'>('primaryHeader')

  // resolve filtered date range min/max values based on filtered Tracking Event data
  const filteredTrackingEventTimeRangeByData = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        if (item.type === LiveEventsCalendarTimelineType.TrackingEvent) {
          acc.minTimeData = item.start_time < acc.minTimeData || acc.minTimeData === 0 ? item.start_time : acc.minTimeData
          acc.maxTimeData = item.end_time > acc.maxTimeData ? item.end_time : acc.maxTimeData
        }

        return acc
      },
      { minTimeData: 0, maxTimeData: 0 } as { minTimeData: number; maxTimeData: number }
    )
  }, [items])

  const itemRenderer = useCallback((itemProps: ItemRendererProps<LiveEventsCalendarTimeLineItem>) => <LiveEventsCalendarItem {...itemProps} />, [])

  const groupRenderer = useCallback(
    (groupProps: ReactCalendarGroupRendererProps<LiveEventsCalendarTimelineGroup>) => (
      <LiveEventsCalendarGroup {...groupProps} groupByArchetype={groupByArchetype} onClick={onGroupClick} onArchetypeClick={onArchetypeClick} />
    ),
    [groupByArchetype, onGroupClick, onArchetypeClick]
  )

  const handleHorizontalLineClassNamesForGroup = useCallback((group: LiveEventsCalendarTimelineGroup) => {
    if (group.type === LiveEventsCalendarTimelineType.TrackingEventWithGame) {
      const typedGroup = group as TrackingEventWithGameTimelineGroup
      const classes = []
      classes.push(typedGroup.groupIndex % 2 === 0 ? 'group-even' : 'group-odd')
      typedGroup.firstOfGroup && classes.push('first-of-group')
      return [
        ...classes,
        group.type,
        typedGroup.tagGroupId && typedGroup.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES ? `${typedGroup.type}--SpecialEvent` : '',
      ]
    } else {
      const typedGroup = group as TrackingEventWithGameTimelineGroup
      return [
        typedGroup.type,
        typedGroup.tagGroupId && typedGroup.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES ? `${typedGroup.type}--SpecialEvent` : '',
      ]
    }
  }, [])

  const formatTimelineDateHeaders = useCallback(
    ([timeStart, timeEnd]: [Dayjs, Dayjs], unit: Unit, labelWidth: number) => {
      const formatOptions = userLanguage === 'zh' || userLanguage === 'ja' ? defaultHeaderFormatsZHJA : defaultHeaderFormatsEN
      const resolvedUnit = unit as 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
      let format
      if (labelWidth >= 175) {
        format = formatOptions[resolvedUnit]?.['long']
      } else if (labelWidth >= 125) {
        format = formatOptions[resolvedUnit]?.['mediumLong']
      } else if (labelWidth >= 50) {
        format = formatOptions[resolvedUnit]?.['medium']
      } else {
        format = formatOptions[resolvedUnit]?.['short'] || 'll'
      }
      return timeStart.format(format)
    },
    [userLanguage]
  )

  const formatSecondaryHeader = useCallback(
    ([timeStart, timeEnd]: [any, any], unit: Unit, labelWidth: number) => {
      // this needs to be inside a timeout since the calendar component calls its callbacks directly on render function
      headerTimeoutRef.current = window.setTimeout(() => setPrimaryUnit(getNextCalendarHeaderUnit(unit)), 0)
      return formatTimelineDateHeaders([timeStart, timeEnd], unit, labelWidth)
    },
    [formatTimelineDateHeaders]
  )

  useEffect(() => () => clearTimeout(headerTimeoutRef.current), [])

  // track mouse down time to determine if it's a click or a drag
  useEffect(() => {
    const handleMouseDown = () => {
      mouseDownTime.current = +new Date()
    }

    const element = scrollRef.current
    element?.addEventListener('mousedown', handleMouseDown)

    return () => {
      element?.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  const handleItemClick = useCallback(
    (itemId: Id) => {
      const delay = +new Date() - mouseDownTime.current
      const item = items.find((item) => item.id === itemId)
      if (item && delay <= 300) {
        onItemClick(item as TrackingEventTimelineItem)
      }
    },
    [items, onItemClick]
  )

  const handleWrapperMouseDown = useCallback(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    // Keep keyboard navigation enabled without triggering page jumps.
    if (document.activeElement !== wrapper) {
      wrapper.focus({ preventScroll: true })
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && timeStart && timeEnd && onCalendarTimeRangeChange) {
        e.preventDefault()
        const range = timeEnd - timeStart
        const shift = range * 0.1
        const direction = e.key === 'ArrowLeft' ? -1 : 1
        onCalendarTimeRangeChange(timeStart + direction * shift, timeEnd + direction * shift, () => {}, 'day')
      }
    },
    [timeStart, timeEnd, onCalendarTimeRangeChange]
  )

  const timelineClasses = classNames('LiveEventsCalendar__Timeline', { calendarHidden })

  useStickyCalendarItemTitles(wrapperRef)

  return (
    <div className="LiveEventsCalendar">
      <Card className="LiveEventsCalendar__Card">
        <div ref={wrapperRef} className={timelineClasses} tabIndex={0} onMouseDown={handleWrapperMouseDown} onKeyDown={handleKeyDown}>
          <ReactCalendarTimeline
            scrollRef={(ref) => (scrollRef.current = ref)}
            groups={groups}
            items={items}
            visibleTimeStart={timeStart}
            visibleTimeEnd={timeEnd}
            onTimeChange={onCalendarTimeRangeChange}
            itemRenderer={itemRenderer}
            groupRenderer={groupRenderer}
            minZoom={minZoom}
            maxZoom={maxZoom}
            sidebarWidth={370}
            lineHeight={lineHeight}
            horizontalLineClassNamesForGroup={handleHorizontalLineClassNamesForGroup}
            onItemClick={handleItemClick}
            onItemSelect={handleItemClick}
            buffer={2}
          >
            <TimelineHeaders className={mobileSize ? '' : 'LiveEventsCalendar__StickyHeader'}>
              {sidebarHeader}
              <DateHeader
                className="LiveEventsCalendar__PrimaryDateHeader"
                unit={primaryUnit as keyof TimelineTimeSteps | 'primaryHeader'}
                height={94}
                labelFormat={formatTimelineDateHeaders as any}
              />
              <DateHeader labelFormat={formatSecondaryHeader as any} height={33} />
            </TimelineHeaders>
            <CustomTimelineMarkers>
              <TimeRangeMarker time={today} tooltipLabel={t('live-events:today')} />
              {trackingEventTimeRange.minTimeData && (
                <TimeRangeMarker time={trackingEventTimeRange.minTimeData} tooltipLabel={t('live-events:tracking_started')} severity="success" />
              )}
              {trackingEventTimeRange.maxTimeData && (
                <TimeRangeMarker time={trackingEventTimeRange.maxTimeData} tooltipLabel={t('live-events:tracking_ends')} severity="warning" />
              )}
              {loadedEventsTimeRange &&
                filteredTrackingEventTimeRangeByData.minTimeData > loadedEventsTimeRange.minTimeData &&
                filteredTrackingEventTimeRangeByData.minTimeData && (
                  <TimeRangeMarker
                    time={filteredTrackingEventTimeRangeByData.minTimeData}
                    tooltipLabel={t('live-events:filtered_tracking_started')}
                    styleOverrides={{ backgroundColor: '#273143' }}
                  />
                )}
              {loadedEventsTimeRange &&
                filteredTrackingEventTimeRangeByData.maxTimeData < loadedEventsTimeRange.maxTimeData &&
                filteredTrackingEventTimeRangeByData.maxTimeData && (
                  <TimeRangeMarker
                    time={filteredTrackingEventTimeRangeByData.maxTimeData}
                    tooltipLabel={t('live-events:filtered_tracking_ends')}
                    styleOverrides={{ backgroundColor: '#273143' }}
                  />
                )}
            </CustomTimelineMarkers>
          </ReactCalendarTimeline>
        </div>
        {isLoading ? (
          <GRCircularProgress circularProgressProps={{ size: 24 }} m={1} />
        ) : (
          (groups.length === 0 || (groups.length === 1 && groups[0]?.id === EMPTY_CALENDAR_GROUP_ID)) &&
          items.length === 0 &&
          showEmptyCalendarIndicator && (
            <Typography className="LiveEventsCalendar__Info" variant="body2" m={1} textAlign="center">
              {t('live-events:no_live_events_found_for_selected_filters')}
            </Typography>
          )
        )}
      </Card>
    </div>
  )
}

export default LiveEventsCalendar

export const defaultHeaderFormatsEN = {
  year: {
    long: 'YYYY',
    mediumLong: 'YYYY',
    medium: 'YYYY',
    short: 'YY',
  },
  month: {
    long: 'MMMM YYYY',
    mediumLong: 'MMMM',
    medium: 'MMMM',
    short: 'MM/YY',
  },
  week: {
    long: 'w',
    mediumLong: 'w',
    medium: 'w',
    short: 'w',
  },
  day: {
    long: 'ddd, ll',
    mediumLong: 'ddd, ll',
    medium: 'dd D',
    short: 'D',
  },
  hour: {
    long: 'dddd, LL, HH:00',
    mediumLong: 'L, HH:00',
    medium: 'HH:00',
    short: 'HH',
  },
  minute: {
    long: 'HH:mm',
    mediumLong: 'HH:mm',
    medium: 'HH:mm',
    short: 'mm',
  },
}

export const defaultHeaderFormatsZHJA = {
  year: {
    long: 'YYYY年',
    mediumLong: 'YYYY年',
    medium: 'YYYY年',
    short: 'YY年',
  },
  month: {
    long: 'YYYY年M月',
    mediumLong: 'M月',
    medium: 'M月',
    short: 'YY年MM月',
  },
  week: {
    long: 'w',
    mediumLong: 'w',
    medium: 'w',
    short: 'w',
  },
  day: {
    long: 'D (dd)',
    mediumLong: 'D (dd)',
    medium: 'D (dd)',
    short: 'D (dd)',
  },
  hour: {
    long: 'dddd, LL, HH:00',
    mediumLong: 'L, HH:00',
    medium: 'HH:00',
    short: 'HH',
  },
  minute: {
    long: 'HH:mm',
    mediumLong: 'HH:mm',
    medium: 'HH:mm',
    short: 'mm',
  },
}
